/**
 * Escribir un cuestionario importado en la base de datos, **de forma atómica**.
 *
 * La validación previa reduce el riesgo pero no lo elimina: entre validar y escribir puede fallar
 * la red o una restricción. Por eso, si algo falla después de crear el cuestionario, **se borra**,
 * apoyándose en el borrado en cascada que ya define el esquema.
 *
 * No es tan elegante como una transacción, pero es lo que hay sin poner un endpoint en el
 * servidor solo para esto, y deja el mismo resultado observable: o está todo, o no está nada.
 */

import type { QuizDraft } from '@clica/shared';
import { supabase } from '../auth/supabase.js';
import { createQuiz, deleteQuiz, type Quiz } from './api.js';

export type ImportOutcome =
  | { readonly ok: true; readonly quiz: Quiz }
  | { readonly ok: false; readonly reason: 'writeFailed' };

/**
 * Crea el cuestionario, sus preguntas y sus opciones.
 *
 * Las preguntas se insertan de una vez y las opciones también: son dos escrituras, no dos por
 * pregunta. Con veinte preguntas la diferencia entre dos peticiones y cuarenta y una se nota.
 */
export async function importQuiz(
  ownerId: string,
  subjectId: string,
  draft: QuizDraft,
): Promise<ImportOutcome> {
  let quiz: Quiz;
  try {
    quiz = await createQuiz(ownerId, subjectId, draft.title);
  } catch {
    return { ok: false, reason: 'writeFailed' };
  }

  try {
    if (draft.description !== null || Object.keys(draft.settings).length > 0) {
      const { error } = await supabase
        .from('quizzes')
        .update({ description: draft.description, default_settings: draft.settings })
        .eq('id', quiz.id);
      if (error !== null) throw error;
    }

    const { data: inserted, error: questionsError } = await supabase
      .from('questions')
      .insert(
        draft.questions.map((question, position) => ({
          quiz_id: quiz.id,
          position,
          type: question.type,
          prompt_md: question.prompt_md,
          hint_md: question.hint_md,
          time_limit_s: question.time_limit_s,
          hint_after_s: question.hint_after_s,
          shuffle_options: question.shuffle_options,
          points_multiplier: question.points_multiplier,
        })),
      )
      .select('id, position');

    if (questionsError !== null) throw questionsError;

    const rows = (inserted ?? []) as { id: string; position: number }[];
    const idByPosition = new Map(rows.map((row) => [row.position, row.id]));

    // Volver con menos filas de las enviadas no da error, pero significa que hay preguntas sin
    // identificador: sus opciones se quedarían fuera y el profesor acabaría con un cuestionario
    // incompleto que la aplicación da por bueno. Es un fallo de escritura como cualquier otro.
    if (idByPosition.size !== draft.questions.length) {
      throw new Error('la inserción de preguntas devolvió menos filas de las enviadas');
    }

    const options = draft.questions.flatMap((question, position) => {
      const questionId = idByPosition.get(position);
      if (questionId === undefined) return [];
      return question.options.map((option, optionPosition) => ({
        question_id: questionId,
        position: optionPosition,
        text_md: option.text_md,
        is_correct: option.is_correct,
      }));
    });

    const { error: optionsError } = await supabase.from('options').insert(options);
    if (optionsError !== null) throw optionsError;

    return { ok: true, quiz };
  } catch {
    // Deshacer. Borrar el cuestionario se lleva sus preguntas y opciones en cascada, así que
    // basta con esto para que no quede nada a medias.
    await deleteQuiz(quiz.id).catch(() => undefined);
    return { ok: false, reason: 'writeFailed' };
  }
}
